import React from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import HeroBadge from "@/components/HeroBadge";
import useScrollReveal from "@/hooks/useScrollReveal";
import { Gift, Users, ArrowRight, CheckCircle2, Phone, Sparkles } from "lucide-react";

export default function Referral() {
  useScrollReveal();
  return (
    <SiteLayout>
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-12 relative">
          <div className="max-w-3xl reveal">
            <HeroBadge>Discount Codes · <span className="text-[#A53A2A]">A little extra for your family</span></HeroBadge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.04]">
              Have a discount code? <span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">Apply &amp; save.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#5C6B7A]">
              If you&#39;ve been given a discount code by a Nirmal Hospital team member or an existing NPC member, simply enter it during registration to unlock 5% off your membership.
            </p>
          </div>
        </div>
      </section>

      {/* Benefit card — customer facing only */}
      <section className="mx-auto max-w-4xl px-6 lg:px-10 py-12">
        <div className="reveal lux-card rounded-3xl p-8 lg:p-10">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#A53A2A] to-[#7A2A1E] text-white grid place-items-center"><Gift className="h-6 w-6" /></div>
          <h3 className="mt-5 font-display text-2xl font-bold text-[#0F2540]">Exclusive benefits for new members</h3>
          <p className="mt-3 text-[#5C6B7A]">
            When you apply a valid discount code during checkout, you&#39;ll receive a 5% discount on your plan. The discount is auto-reflected in your order summary — so you&#39;ll always know what you&#39;re getting.
          </p>
          <ul className="mt-6 space-y-2.5 text-sm text-[#0F2540]">
            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#1F7A3D] flex-shrink-0" /> Apply any valid discount code at checkout</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#1F7A3D] flex-shrink-0" /> 5% discount auto-reflected in your order summary</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#1F7A3D] flex-shrink-0" /> No hidden conditions — confirmed before you pay</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#1F7A3D] flex-shrink-0" /> Your digital NPC card is issued instantly</li>
          </ul>
        </div>
      </section>

      {/* How it works — 3 steps, customer flow only */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="reveal lux-card rounded-3xl p-8 lg:p-10">
          <span className="overline">How to apply</span>
          <h3 className="mt-2 font-display text-2xl font-bold text-[#0F2540]">In three simple steps.</h3>
          <ol className="mt-6 grid md:grid-cols-3 gap-5 text-sm text-[#0F2540]">
            {[
              ["1.", "Receive your code", "Your code is shared by a Nirmal Hospital representative or an existing NPC member."],
              ["2.", "Apply at checkout", "Enter the code in the Discount Code step of the Join NPC flow. We'll validate it instantly."],
              ["3.", "Enjoy your benefit", "Your 5% discount is confirmed on the review screen before payment."],
            ].map(([n, t, d]) => (
              <li key={t} className="rounded-2xl border border-[#E1E8F0] p-5 bg-white">
                <div className="font-display text-3xl font-bold text-[#A53A2A]">{n}</div>
                <div className="mt-2 font-semibold text-[#0F2540]">{t}</div>
                <div className="text-[#5C6B7A] mt-1">{d}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Existing NPC members — they can share their NPC ID */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="reveal lux-card rounded-3xl p-8 lg:p-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="overline">Already an NPC member?</span>
            <h3 className="mt-2 font-display text-2xl lg:text-3xl font-bold text-[#0F2540]">Share your NPC ID with friends &amp; family.</h3>
            <p className="mt-3 text-[#5C6B7A] max-w-xl">
              Existing NPC members can share their own NPC member ID as a discount code with loved ones who are joining. When they apply it at checkout, they&#39;ll receive 5% off their plan.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link to="/dashboard" className="btn-ghost"><Users className="h-4 w-4" /> View my NPC ID</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="rounded-[2rem] mesh-dark p-10 lg:p-14 relative overflow-hidden reveal">
          <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="overline text-[#C89B3C]">Questions?</span>
              <h3 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-white">Not sure about your discount code?</h3>
              <p className="mt-3 text-white/80 max-w-xl">Our Privilege Card team is happy to help you verify a code or walk you through the Join flow. Talk to us — we speak Gujarati, Hindi and English.</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
              <Link to="/join" className="bg-white text-[#0F2540] px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-[#C89B3C] transition"><Sparkles className="h-4 w-4" /> Join NPC <ArrowRight className="h-4 w-4" /></Link>
              <a href="tel:+916357033301" className="border border-white/40 text-white px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-white/10 transition"><Phone className="h-4 w-4" /> +91-63570-33301</a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
